<template>
  <!-- Desktop Sidebar -->
  <nav
    id="sidebarDesktop"
    class="navigation-desktop d-none d-lg-flex flex-column w-100 vh-100 d-flex"
    :class="{
      'navigation-desktop--development': !isProduction,
      'navigation-desktop--production': isProduction,
    }"
  >
    <!-- Logo -->
    <RouterLink to="/" class="navigation-desktop__logo text-decoration-none">
      <h2 class="navigation-desktop__logo-text text-white">
        {{ APP_NAME }}
      </h2>
    </RouterLink>

    <!-- Search -->
    <NavigationSearch />

    <!-- General -->
    <NavigationSideBarTitle title="General" />

    <div class="nav flex-column">
      <template v-for="link in SIDEBAR_GENERAL_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>
    </div>

    <!-- Divider -->
    <div class="navigation-desktop__sidebar-divider"></div>

    <!-- Configuration -->
    <NavigationSideBarTitle title="Configuration" />

    <div class="nav flex-column">
      <template v-for="link in SIDEBAR_CONFIGURATION_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>
    </div>

    <!-- Divider -->
    <div class="navigation-desktop__sidebar-divider"></div>

    <!-- Diagnostics -->
    <NavigationSideBarTitle title="Diagnostics" />

    <div class="nav flex-column">
      <template v-for="link in SIDEBAR_DIAGNOSTICS_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>
    </div>

    <!-- To push the User Section always to the bottom of the navigation menu -->
    <div class="flex-grow-1"></div>
    <!-- User Card -->
    <NavigationSidebarUser :display-name="displayName" :staging-color="stageColoring" />
  </nav>
</template>

<script setup lang="ts">
import {
  APP_NAME,
  SIDEBAR_CONFIGURATION_LINKS,
  SIDEBAR_DIAGNOSTICS_LINKS,
  SIDEBAR_GENERAL_LINKS,
} from '@/components/navigation/constants/NAVIGATION';
import NavigationMainLinks from '@/components/navigation/NavigationMainLinks.vue';
import NavigationSearch from '@/components/navigation/NavigationSearch.vue';
import NavigationSideBarTitle from '@/components/navigation/NavigationSideBarTitle.vue';
import NavigationSidebarUser from '@/components/navigation/NavigationSidebarUser.vue';
import { getStageColor } from '@/utils';

type Props = {
  displayName: string;
  isProduction: boolean;
};

defineProps<Props>();

const stageColoring = getStageColor();
</script>

<style lang="scss" scoped>
.navigation-desktop__logo {
  margin-bottom: 1rem;
}

.navigation-desktop__logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.05;
  margin: 0;
  max-width: 8.75rem;
}

.navigation-desktop {
  min-width: 16.875rem;
  max-width: 16.875rem;
  padding: 1.5rem 1rem;
  border-right: 0.0625rem solid rgba(255, 255, 255, 0.08);
  overflow-y: auto;
}

.navigation-desktop--development {
  background: linear-gradient(180deg, #612336 0%, #511e2b 35%, #301118 100%);
}

.navigation-desktop--production {
  background: linear-gradient(180deg, #243763 0%, #1e2b51 35%, #15203b 100%);
}

/* Scrollbar */
.navigation-desktop::-webkit-scrollbar {
  width: 0.125rem;
}

.navigation-desktop::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 0.625rem;
}

.navigation-desktop__sidebar-divider {
  height: 0.0625rem;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.75rem 0;
}

.navigation-desktop--production :deep(.navigation-main-links.active) {
  background: linear-gradient(90deg, #2c437a 0%, #38538f 100%);
  box-shadow: 0 0.625rem 1.375rem rgb(34 52 95);
}
</style>
